/**
 * DragHandle Component
 *
 * Reusable drag handle indicator for drag-and-drop operations using @dnd-kit.
 * Provides consistent styling and accessibility.
 */

import React from 'react';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { DragIndicator } from '@mui/icons-material';
import { IconButton } from '@mui/material';

interface DragHandleProps {
  /** @dnd-kit listeners from useSortable */
  listeners?: DraggableSyntheticListeners;
  /** @dnd-kit attributes from useSortable */
  attributes?: DraggableAttributes;
  /** Whether dragging is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

export const DragHandle: React.FC<DragHandleProps> = ({
  listeners,
  attributes,
  disabled = false,
  size = 'medium',
}) => {
  return (
    <IconButton
      {...listeners}
      {...attributes}
      disabled={disabled}
      sx={{
        borderRadius: 0,
        cursor: disabled ? 'default' : 'grab',
        '&:active': {
          cursor: disabled ? 'default' : 'grabbing',
        },
        color: disabled ? 'text.disabled' : 'text.secondary',
        '&:hover': disabled
          ? {}
          : {
              color: 'primary.main',
              bgcolor: 'action.hover',
            },
      }}
      aria-label="Drag to reorder"
    >
      <DragIndicator fontSize={size} />
    </IconButton>
  );
};
